import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Landing page components
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import TrustBar from "@/components/TrustBar";
import StatsSection from "@/components/StatsSection";
import TargetAudience from "@/components/TargetAudience";
import Services from "@/components/Services";
import BeratungsThemen from "@/components/BeratungsThemen";
import HowItWorks from "@/components/HowItWorks";
import BookingSection from "@/components/OwnBookingSection";
import RegionenNRW from "@/components/RegionenNRW";
import AboutUs from "@/components/AboutUs";
import Ratgeber from "@/components/Ratgeber";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import StickyCTA from "@/components/StickyCTA";
import WhatsAppButton from "@/components/WhatsAppButton";
import Impressum from "@/components/Impressum";
import Datenschutz from "@/components/Datenschutz";
import CookieBanner from "@/components/CookieBanner";

import RechnerTeaser from "@/components/RechnerTeaser";
import PhoneStrip from "@/components/PhoneStrip";
import PhoneAlternative from "@/components/PhoneAlternative";

// CRM components
import CRMLogin from "@/components/crm/CRMLogin";
import CRMLayout from "@/components/crm/CRMLayout";
import CRMCustomerDetail from "@/components/crm/CRMCustomerDetail";
import CRMBeratungsbogen from "@/components/crm/CRMBeratungsbogen";
import CRMVideoCall from "@/components/crm/CRMVideoCall";
import CRMSchulungen from "@/components/crm/CRMSchulungen";
import CRMStart from "@/components/crm/CRMStart";
import CRMAlltag from "@/components/crm/CRMAlltag";
import CRMVorgang from "@/components/crm/CRMVorgang";
import CRMBeratung from "@/components/crm/CRMBeratung";
import CRMVerwaltung from "@/components/crm/CRMVerwaltung";
import CRMKalender from "@/components/crm/CRMKalender";
import CRMGeschaeftsfuehrung from "@/components/crm/CRMGeschaeftsfuehrung";
import BewerbungPage from "@/components/BewerbungPage";
import { MeinAuftrag } from "@/components/einsatz/MeineAuftraege";
import Aufgaben from "@/components/einsatz/Aufgaben";
import MitarbeiterAbwesenheiten from "@/components/einsatz/Abwesenheiten";
import { MitarbeiterLogin, MitarbeiterLayout, MitarbeiterStart, MitarbeiterVerfuegbarkeit, MitarbeiterPasswort } from "@/components/einsatz/MitarbeiterBereich";
import MitarbeiterAktivieren from "@/components/einsatz/MitarbeiterAktivieren";
import MeineSchulungen from "@/components/einsatz/MeineSchulungen";
import ArbeitsschutzUnterweisung from "@/components/einsatz/ArbeitsschutzUnterweisung";

// Schulungen (B2B)
import SchulungenLanding from "@/components/schulungen/SchulungenLanding";
import SchulungenLogin from "@/components/schulungen/SchulungenLogin";
import Firmenregistrierung from "@/components/schulungen/Firmenregistrierung";
import FirmenPortal from "@/components/schulungen/FirmenPortal";
import EinladungEinloesen from "@/components/schulungen/EinladungEinloesen";
import TeilnehmerLayout from "@/components/schulungen/TeilnehmerLayout";
import TeilnehmerDashboard from "@/components/schulungen/TeilnehmerDashboard";
import ModulView from "@/components/schulungen/ModulView";
import TestView from "@/components/schulungen/TestView";
import TermineView from "@/components/schulungen/TermineView";
import { PasswortVergessen, PasswortReset } from "@/components/schulungen/PasswortReset";
import { homeForRole } from "@/components/schulungen/roles";

// Public video join page
import VideoJoinPage from "@/components/VideoJoinPage";

// Customer portal
import PortalLogin from "@/components/portal/PortalLogin";
import PortalDashboard from "@/components/portal/PortalDashboard";

// Blog
import BlogList from "@/components/blog/BlogList";
import BlogArticle from "@/components/blog/BlogArticle";
import SozialraumbudgetRechner from "@/components/blog/SozialraumbudgetRechner";
import VerhinderungspflegeAssistent from "@/components/blog/VerhinderungspflegeAssistent";
import PflegeleistungenFinder from "@/components/blog/PflegeleistungenFinder";
import AlltagsbegleitungPage from "@/components/AlltagsbegleitungPage";
import AlltagsbegleitungGrefrath from "@/components/AlltagsbegleitungGrefrath";
import AlltagsbegleitungRheinberg from "@/components/AlltagsbegleitungRheinberg";
import DesignVorschau from "@/pages/DesignVorschau";
import { MitarbeiterStartNeu, KalenderAufgaben, Dokumente, Profil, Ausfall } from "@/components/einsatz/Phase3Portal";
import TourLive from "@/components/einsatz/TourLive";
import DokumentScanner from "@/components/einsatz/DokumentScanner";
import CRMPosteingang from "@/components/crm/CRMPosteingang";

import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=no auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("lw_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setUser({ ...res.data, token }); setLoading(false); })
        .catch(() => { localStorage.removeItem("lw_token"); setUser(false); setLoading(false); });
    } else {
      setUser(false);
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const userData = { ...res.data, token: res.data.token };
    localStorage.setItem("lw_token", res.data.token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("lw_token");
    setUser(false);
  };

  const setSession = (data) => {
    localStorage.setItem("lw_token", data.token);
    setUser({ ...data, token: data.token });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

const STAFF_ROLES = ["admin", "kursleitung", "teamleitung"];

function ProtectedRoute({ children, roles = STAFF_ROLES, loginPath = "/app/login" }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to={loginPath} replace />;
  const role = user.role || "admin";
  if (!roles.includes(role)) return <Navigate to={homeForRole(role)} replace />;
  return children;
}

// Landing Page
function LandingPage() {
  const [showSticky, setShowSticky] = useState(false);
  const [impressumOpen, setImpressumOpen] = useState(false);
  const [datenschutzOpen, setDatenschutzOpen] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sprunglink (z. B. /#termin-buchen): nach dem Rendern zuverlässig scrollen, auch wenn Inhalte nachladen
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const timers = [0, 300, 900, 1800].map((ms) => setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: ms === 0 ? "auto" : "smooth", block: "start" });
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="app-root">
      <Header />
      <div className="pt-[108px] xl:pt-[84px]"><PhoneStrip /></div>
      <main>
        <Hero />
        <TrustBar />
        <StatsSection />
        <TargetAudience />
        <Services />
        <BeratungsThemen />
        <HowItWorks />
        <PhoneAlternative />
        <BookingSection />
        <RegionenNRW />
        <AboutUs />
        <Ratgeber />
        <RechnerTeaser />
        <FAQ />
        <Contact />
      </main>
      <Footer onOpenImpressum={() => setImpressumOpen(true)} onOpenDatenschutz={() => setDatenschutzOpen(true)} />
      <StickyCTA visible={showSticky} />
      <WhatsAppButton />
      <Impressum open={impressumOpen} onOpenChange={setImpressumOpen} />
      <Datenschutz open={datenschutzOpen} onOpenChange={setDatenschutzOpen} />
      <CookieBanner onOpenDatenschutz={() => setDatenschutzOpen(true)} />
    </div>
  );
}

// Google-Tag SPA Pageview Tracker (einzige Quelle für page_view – GA4 + Google Ads) + Dynamic Canonical + OG Meta
function GA4PageviewTracker() {
  const location = useLocation();
  const lastTracked = useRef(null);
  useEffect(() => {
    const path = location.pathname;
    const fullUrl = "https://lehnen-weinacht-digitalberatung.de" + path;

    // Update canonical
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", fullUrl);

    // Update og:url
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", fullUrl);

    // page_view an GA4 + Google Ads (einmal pro URL-Wechsel)
    const key = path + location.search;
    if (lastTracked.current === key) return;
    lastTracked.current = key;
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: key,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GA4PageviewTracker />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/alltagsbegleitung" element={<><Header /><AlltagsbegleitungPage /></>} />
          <Route path="/alltagsbegleitung-grefrath" element={<AlltagsbegleitungGrefrath />} />
          <Route path="/alltagsbegleitung-viersen" element={<Navigate to="/alltagsbegleitung" replace />} />
          <Route path="/alltagsbegleitung-kempen" element={<Navigate to="/alltagsbegleitung" replace />} />
          <Route path="/alltagsbegleitung-rheinberg" element={<AlltagsbegleitungRheinberg />} />
          <Route path="/design-vorschau" element={<DesignVorschau />} />
          <Route path="/bewerbung/:slug" element={<BewerbungPage />} />
          <Route path="/bewerbung" element={<BewerbungPage />} />
          <Route path="/app/login" element={<CRMLogin />} />
          <Route path="/app" element={<ProtectedRoute><CRMLayout /></ProtectedRoute>}>
            <Route index element={<CRMStart />} />
            <Route path="posteingang" element={<CRMPosteingang />} />
            <Route path="alltag" element={<CRMAlltag />} />
            <Route path="alltag/vorgang/:vid" element={<CRMVorgang />} />
            <Route path="beratung" element={<CRMBeratung />} />
            <Route path="customers/:id" element={<CRMCustomerDetail />} />
            <Route path="beratung/:id" element={<CRMBeratungsbogen />} />
            <Route path="beratung/new/:appointmentId" element={<CRMBeratungsbogen />} />
            <Route path="video/:appointmentId" element={<CRMVideoCall />} />
            <Route path="schulungen" element={<CRMSchulungen />} />
            <Route path="verwaltung" element={<CRMVerwaltung />} />
            <Route path="kalender" element={<CRMKalender />} />
            <Route path="geschaeftsfuehrung" element={<CRMGeschaeftsfuehrung />} />
            <Route path="dashboard" element={<Navigate to="/app/beratung" replace />} />
            <Route path="appointments" element={<Navigate to="/app/beratung?tab=termine" replace />} />
            <Route path="customers" element={<Navigate to="/app/beratung?tab=kunden" replace />} />
            <Route path="anfragen" element={<Navigate to="/app/beratung?tab=anfragen" replace />} />
            <Route path="mitarbeiter" element={<Navigate to="/app/verwaltung" replace />} />
            <Route path="zugaenge" element={<Navigate to="/app/verwaltung?tab=zugaenge" replace />} />
            <Route path="gebiete" element={<Navigate to="/app/verwaltung?tab=gebiete" replace />} />
            <Route path="controlling" element={<Navigate to="/app/verwaltung?tab=controlling" replace />} />
            <Route path="einsatzkunden" element={<Navigate to="/app/alltag?tab=kunden" replace />} />
            <Route path="verfuegbarkeiten" element={<Navigate to="/app/alltag?tab=team" replace />} />
          </Route>
          <Route path="/mitarbeiter/login" element={<MitarbeiterLogin />} />
          <Route path="/mitarbeiter/aktivieren/:token" element={<MitarbeiterAktivieren />} />
          <Route path="/mitarbeiter" element={<ProtectedRoute roles={["mitarbeiter", "teamleitung"]} loginPath="/mitarbeiter/login"><MitarbeiterLayout /></ProtectedRoute>}>
            <Route index element={<MitarbeiterStartNeu />} />
            <Route path="aufgaben" element={<Navigate to="/mitarbeiter/kalender?tab=aufgaben" replace />} />
            <Route path="kalender" element={<KalenderAufgaben />} />
            <Route path="dokumente" element={<Dokumente />} />
            <Route path="profil" element={<Profil />} />
            <Route path="ausfall" element={<Ausfall />} />
            <Route path="tour/:rid" element={<TourLive />} />
            <Route path="scan" element={<DokumentScanner />} />
            <Route path="boegen" element={<Navigate to="/mitarbeiter/aufgaben?tab=boegen" replace />} />
            <Route path="boegen/:aid" element={<MeinAuftrag />} />
            <Route path="kunden" element={<Navigate to="/mitarbeiter/aufgaben" replace />} />
            <Route path="kunden/*" element={<Navigate to="/mitarbeiter/aufgaben" replace />} />
            <Route path="verfuegbarkeit" element={<Navigate to="/mitarbeiter/profil" replace />} />
            <Route path="abwesenheiten" element={<MitarbeiterAbwesenheiten />} />
            <Route path="passwort" element={<MitarbeiterPasswort />} />
            <Route path="schulungen" element={<MeineSchulungen />} />
            <Route path="schulungen/arbeitsschutz" element={<ArbeitsschutzUnterweisung />} />
          </Route>
          <Route path="/schulungen" element={<SchulungenLanding />} />
          <Route path="/schulungen/firmenregistrierung" element={<Firmenregistrierung />} />
          <Route path="/schulungen/login" element={<SchulungenLogin />} />
          <Route path="/schulungen/passwort-vergessen" element={<PasswortVergessen />} />
          <Route path="/schulungen/passwort-reset/:token" element={<PasswortReset />} />
          <Route path="/schulungen/firma" element={<ProtectedRoute roles={["firma"]} loginPath="/schulungen/login"><FirmenPortal /></ProtectedRoute>} />
          <Route path="/schulungen/einladung/:token" element={<EinladungEinloesen />} />
          <Route path="/schulungen/teilnehmer" element={<ProtectedRoute roles={["teilnehmer"]} loginPath="/schulungen/login"><TeilnehmerLayout /></ProtectedRoute>}>
            <Route index element={<TeilnehmerDashboard />} />
            <Route path="modul/:modulId" element={<ModulView />} />
            <Route path="test" element={<TestView />} />
            <Route path="termine" element={<TermineView />} />
          </Route>
          {/* Public video join page for customers */}
          <Route path="/video/:roomId" element={<VideoJoinPage />} />
          {/* Customer portal */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<PortalDashboard />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/sozialraumbudget-rechner" element={<SozialraumbudgetRechner />} />
          <Route path="/blog/verhinderungspflege-antrag" element={<VerhinderungspflegeAssistent />} />
          <Route path="/blog/pflegeleistungen-finder" element={<PflegeleistungenFinder />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
        </Routes>
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
